import * as React from 'react'
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  PaginationState,
  OnChangeFn,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  RowSelectionState,
} from '@tanstack/react-table'
import { ArrowUpDown, ChevronDown, Search, Filter, Check } from 'lucide-react'

import { Button } from './button'
import { Input } from './input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './table'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './dropdown-menu'
import { Checkbox } from './checkbox'

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[]
  data: TData[]
  onRowSelectionChange?: (selectedRows: TData[]) => void
  searchPlaceholder?: string
  searchColumn?: string
  enableRowSelection?: boolean
  enableColumnVisibility?: boolean
  enablePagination?: boolean
  pageSize?: number
  pageSizeOptions?: number[]
  toolbarActions?: React.ReactNode
  // Server-side pagination props
  manualPagination?: boolean
  pageCount?: number
  pagination?: PaginationState
  onPaginationChange?: OnChangeFn<PaginationState>
  // Server-side sorting props
  manualSorting?: boolean
  sorting?: SortingState
  onSortingChange?: OnChangeFn<SortingState>
  // Server-side search props
  manualSearch?: boolean
  searchValue?: string
  onSearchChange?: (value: string) => void
  // Row identity function for stable keys
  getRowId?: (row: TData) => string
  // Unique ID for persisting column visibility to localStorage
  tableId?: string
}

// Helper to get localStorage key for column visibility
const getColumnVisibilityKey = (tableId: string) => `dataTable_columnVisibility_${tableId}`

// Helper to load column visibility from localStorage
const loadColumnVisibility = (tableId?: string): VisibilityState => {
  if (!tableId) return {}
  try {
    const stored = localStorage.getItem(getColumnVisibilityKey(tableId))
    return stored ? JSON.parse(stored) : {}
  } catch {
    return {}
  }
}

export function DataTable<TData, TValue>({
  columns,
  data,
  onRowSelectionChange,
  searchPlaceholder = 'Search...',
  searchColumn,
  enableRowSelection = true,
  enableColumnVisibility = true,
  enablePagination = true,
  pageSize = 10,
  pageSizeOptions = [10, 25, 50, 100],
  toolbarActions,
  manualPagination = false,
  pageCount,
  pagination: controlledPagination,
  onPaginationChange,
  manualSorting = false,
  sorting: controlledSorting,
  onSortingChange,
  manualSearch = false,
  searchValue: controlledSearchValue,
  onSearchChange,
  getRowId,
  tableId,
}: DataTableProps<TData, TValue>) {
  const [internalSorting, setInternalSorting] = React.useState<SortingState>([])
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([])
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>(() => loadColumnVisibility(tableId))
  const [rowSelection, setRowSelection] = React.useState<RowSelectionState>({})
  const [internalSearchValue] = React.useState('')
  
  // Persist column visibility to localStorage whenever it changes
  React.useEffect(() => {
    if (tableId && Object.keys(columnVisibility).length > 0) {
      try {
        localStorage.setItem(getColumnVisibilityKey(tableId), JSON.stringify(columnVisibility))
      } catch {
        // Ignore localStorage errors
      }
    }
  }, [columnVisibility, tableId])
  
  // Use controlled sorting if provided
  const sorting = manualSorting && controlledSorting ? controlledSorting : internalSorting
  const setSorting: OnChangeFn<SortingState> = React.useCallback((updater) => {
    if (manualSorting && onSortingChange) {
      onSortingChange(updater)
    } else {
      setInternalSorting(updater)
      if (onSortingChange) {
        onSortingChange(updater)
      }
    }
  }, [manualSorting, onSortingChange])
  
  // Use controlled search if provided
  const searchValue = manualSearch && controlledSearchValue !== undefined ? controlledSearchValue : internalSearchValue
  
  // Local input state for manual search - only updates parent on button click or Enter
  const [localSearchInput, setLocalSearchInput] = React.useState(searchValue)
  
  // Sync local input when controlled value changes externally
  React.useEffect(() => {
    if (manualSearch) {
      setLocalSearchInput(controlledSearchValue ?? '')
    }
  }, [controlledSearchValue, manualSearch])
  
  // Handler for triggering search
  const handleSearch = React.useCallback(() => {
    if (manualSearch && onSearchChange) {
      onSearchChange(localSearchInput)
    }
  }, [manualSearch, onSearchChange, localSearchInput])
  
  // Handle Enter key press
  const handleKeyDown = React.useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSearch()
    }
  }, [handleSearch])
  
  // Use controlled pagination if provided, otherwise use internal state
  const [internalPagination, setInternalPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: pageSize,
  })
  
  const pagination = manualPagination && controlledPagination ? controlledPagination : internalPagination
  
  // Handle pagination changes - for manual pagination, use the provided callback
  // For client-side pagination, use internal state but also notify parent if callback provided
  const setPagination: OnChangeFn<PaginationState> = React.useCallback((updater) => {
    if (manualPagination && onPaginationChange) {
      onPaginationChange(updater)
    } else {
      setInternalPagination(updater)
      // Also notify parent for client-side pagination if callback provided
      if (onPaginationChange) {
        onPaginationChange(updater)
      }
    }
  }, [manualPagination, onPaginationChange])

  const table = useReactTable({
    data,
    columns,
    pageCount: manualPagination ? pageCount : undefined,
    getRowId,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: enablePagination ? getPaginationRowModel() : undefined,
    getSortedRowModel: manualSorting ? undefined : getSortedRowModel(),
    getFilteredRowModel: manualSearch ? undefined : getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    manualPagination,
    manualSorting,
    manualFiltering: manualSearch,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      pagination,
    },
    enableRowSelection,
  })

  // Notify parent component when row selection changes
  React.useEffect(() => {
    if (onRowSelectionChange) {
      const selectedRows = table.getFilteredSelectedRowModel().rows.map(row => row.original)
      onRowSelectionChange(selectedRows)
    }
  }, [rowSelection, onRowSelectionChange])

  return (
    <div className="data-table-container space-y-4">
      {/* Toolbar */}
      <div className="data-table-toolbar flex items-center justify-between gap-4">
        <div className="flex items-center gap-4 flex-1">
          {/* Search */}
          {(searchColumn || manualSearch) && (
            <div className="search-input-wrapper flex items-center gap-2 flex-1 max-w-md">
              <div className="search-field relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={searchPlaceholder}
                  value={manualSearch ? localSearchInput : ((table.getColumn(searchColumn!)?.getFilterValue() as string) ?? '')}
                  onChange={(event) => {
                    if (manualSearch) {
                      setLocalSearchInput(event.target.value)
                    } else if (searchColumn) {
                      table.getColumn(searchColumn)?.setFilterValue(event.target.value)
                    }
                  }}
                  onKeyDown={manualSearch ? handleKeyDown : undefined}
                  className="pl-9"
                />
              </div>
              {manualSearch && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSearch}
                  className="search-btn"
                >
                  <Search className="h-4 w-4" />
                </Button>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* Custom Toolbar Actions */}
          {toolbarActions}

          {/* Column Visibility */}
          {enableColumnVisibility && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="column-visibility-btn">
                  <ChevronDown className="mr-2 h-4 w-4" />
                  Columns
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-[150px]">
                {table
                  .getAllColumns()
                  .filter((column) => column.getCanHide())
                  .map((column) => {
                    return (
                      <DropdownMenuCheckboxItem
                        key={column.id}
                        className="capitalize"
                        checked={column.getIsVisible()}
                        onCheckedChange={(value) => column.toggleVisibility(!!value)}
                      >
                        {column.id}
                      </DropdownMenuCheckboxItem>
                    )
                  })}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="data-table-wrapper rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  )
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {enablePagination && (
        <div className="data-table-pagination flex items-center justify-between">
          <div className="selection-info flex-1 text-sm text-muted-foreground">
            {enableRowSelection && (
              <span>
                {table.getFilteredSelectedRowModel().rows.length} of{' '}
                {table.getFilteredRowModel().rows.length} row(s) selected.
              </span>
            )}
          </div>
          <div className="pagination-controls flex items-center space-x-6 lg:space-x-8">
            <div className="rows-per-page flex items-center space-x-2">
              <p className="text-sm text-muted-foreground">Rows per page</p>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="h-8 w-[70px]">
                    {pagination.pageSize}
                    <ChevronDown className="ml-2 h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="center">
                  {pageSizeOptions.map((size) => (
                    <DropdownMenuItem
                      key={size}
                      onClick={() => {
                        table.setPageSize(size)
                      }}
                      className={pagination.pageSize === size ? 'bg-accent' : ''}
                    >
                      {size}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            <div className="pagination-info flex items-center space-x-2">
              <p className="text-sm font-medium">
                Page {table.getState().pagination.pageIndex + 1} of{' '}
                {table.getPageCount()}
              </p>
            </div>
            <div className="pagination-buttons flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
              >
                Next
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Helper component for sortable column headers
export function SortableHeader({ column, children }: { column: any; children: React.ReactNode }) {
  return (
    <Button
      variant="ghost"
      size="sm"
      className="-ml-3 h-8 data-[state=open]:bg-accent"
      onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
    >
      {children}
      <ArrowUpDown className="ml-2 h-4 w-4" />
    </Button>
  )
}

// Helper component for sortable + filterable column headers
interface FilterableHeaderProps {
  column: any
  children: React.ReactNode
  options: string[]
  selectedValues: string[]
  onFilterChange: (values: string[]) => void
  sortable?: boolean
  /** Optional map of option value to display label (e.g., { 'en': '🇬🇧 English' }) */
  optionLabels?: Record<string, string>
}

export function FilterableHeader({ 
  column, 
  children, 
  options, 
  selectedValues, 
  onFilterChange,
  sortable = true,
  optionLabels
}: FilterableHeaderProps) {
  const [isOpen, setIsOpen] = React.useState(false)
  // Pending state holds selections before applying
  const [pendingValues, setPendingValues] = React.useState<string[]>(selectedValues)
  
  // Sync pending values when dropdown opens or selectedValues change externally
  React.useEffect(() => {
    if (isOpen) {
      setPendingValues(selectedValues)
    }
  }, [isOpen, selectedValues])
  
  const hasActiveFilter = selectedValues.length > 0
  
  const toggleValue = (value: string) => {
    if (pendingValues.includes(value)) {
      setPendingValues(pendingValues.filter(v => v !== value))
    } else {
      setPendingValues([...pendingValues, value])
    }
  }
  
  const selectAll = () => setPendingValues([...options])
  const unselectAll = () => setPendingValues([])
  
  const applyFilter = () => {
    // If all selected or none selected, treat as "show all" (empty array)
    if (pendingValues.length === options.length || pendingValues.length === 0) {
      onFilterChange([])
    } else {
      onFilterChange(pendingValues)
    }
    setIsOpen(false)
  }
  
  const handleOpenChange = (open: boolean) => {
    if (open) {
      setPendingValues(selectedValues.length > 0 ? selectedValues : [...options])
    }
    setIsOpen(open)
  }
  
  return (
    <div className="filterable-header flex items-center gap-1">
      {sortable ? (
        <Button
          variant="ghost"
          size="sm"
          className="-ml-3 h-8 data-[state=open]:bg-accent"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        >
          {children}
          <ArrowUpDown className="ml-1 h-3 w-3" />
        </Button>
      ) : (
        <span className="text-sm font-medium">{children}</span>
      )}
      
      <DropdownMenu open={isOpen} onOpenChange={handleOpenChange}>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="ghost" 
            size="sm" 
            className={`h-6 w-6 p-0 ${hasActiveFilter ? 'text-primary' : 'text-muted-foreground'}`}
          >
            <Filter className={`h-3 w-3 ${hasActiveFilter ? 'fill-primary/20' : ''}`} />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-[180px]" onCloseAutoFocus={(e) => e.preventDefault()}>
          {/* Select All / Unselect All buttons */}
          <div className="select-actions flex gap-1 px-2 py-1.5 border-b">
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-6 px-2 text-xs flex-1"
              onClick={selectAll}
            >
              Select all
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-6 px-2 text-xs flex-1"
              onClick={unselectAll}
            >
              Unselect all
            </Button>
          </div>
          
          {/* Options list */}
          <div className="filter-options max-h-[200px] overflow-y-auto py-1">
            {options.map((option) => (
              <DropdownMenuItem
                key={option}
                onClick={(e) => {
                  e.preventDefault()
                  toggleValue(option)
                }}
                className="cursor-pointer"
              >
                <div className={`mr-2 h-4 w-4 border rounded flex items-center justify-center flex-shrink-0 ${
                  pendingValues.includes(option) 
                    ? 'bg-primary border-primary' 
                    : 'border-muted-foreground'
                }`}>
                  {pendingValues.includes(option) && (
                    <Check className="h-3 w-3 text-primary-foreground" />
                  )}
                </div>
                <span className={optionLabels ? '' : 'capitalize'}>{optionLabels?.[option] ?? option}</span>
              </DropdownMenuItem>
            ))}
          </div>
          
          {/* Apply button */}
          <div className="apply-action px-2 py-1.5 border-t">
            <Button 
              size="sm" 
              className="w-full h-7 text-xs"
              onClick={applyFilter}
            >
              Apply
            </Button>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}

// Helper for checkbox column
export function createSelectionColumn<TData>(): ColumnDef<TData> {
  return {
    id: 'select',
    header: ({ table }) => (
      <Checkbox
        checked={
          table.getIsAllPageRowsSelected() ||
          (table.getIsSomePageRowsSelected() && 'indeterminate')
        }
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label="Select all"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label="Select row"
      />
    ),
    enableSorting: false,
    enableHiding: false,
  }
}

